// src/components/RecordVideo.js
import React, { useState, useRef, useEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import { 
  doc, 
  getDoc, 
  collection, 
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';

import CONFIG from '../config';
import TimerDisplay from './TimerDisplay';
import { logRecordStart, logRerecord } from '../utils/activityLogger';

// UI Components
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Input } from './ui/Input';
import { Alert } from './ui/Alert';
import { 
  Camera, 
  Circle, 
  Square, 
  Save, 
  RefreshCcw, 
  Zap,
  Video
} from 'lucide-react';

const UploadProgress = lazy(() => import('./UploadProgress'));

function RecordVideo() {
  const [recording, setRecording] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [userData, setUserData] = useState(null);
  const [stream, setStream] = useState(null);
  const [deviceOrientation, setDeviceOrientation] = useState('landscape');
  const [timeLeft, setTimeLeft] = useState(0);
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const navigate = useNavigate();

  const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          setUserData(userSnap.data());
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
    return () => stopMediaStream();
  }, []);

  useEffect(() => {
    const handleOrientation = () => setDeviceOrientation(window.innerHeight > window.innerWidth ? 'portrait' : 'landscape');
    handleOrientation();
    window.addEventListener('resize', handleOrientation);
    return () => window.removeEventListener('resize', handleOrientation);
  }, []);

  const stopMediaStream = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const initializeCamera = async () => {
    stopMediaStream();
    try {
      const constraints = {
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      };
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(newStream);
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        videoRef.current.muted = true;
      }
      setCameraReady(true);
      toast.success(CONFIG.ui.toasts.cameraReady);
    } catch (error) {
      toast.error(CONFIG.ui.toasts.couldNotAccessCamera);
    }
  };

  const startRecording = () => {
    if (!cameraReady) return;
    let count = CONFIG.recording.countdownSeconds;
    setCountdown(count);
    logRecordStart().catch(err => console.error(err));
    const intervalId = setInterval(() => {
      count -= 1;
      setCountdown(count);
      if (count === 0) {
        clearInterval(intervalId);
        setCountdown(null);
        beginRecording();
      }
    }, 1000);
  };

  const beginRecording = () => {
    try {
      const tierKey = userData?.subscriptionTier || 'free';
      const tier = CONFIG.tiers[tierKey] || CONFIG.tiers.free;
      const isHD = tier.quality === 'HD';
      const mobile = isMobile();
      
      let mimeType = CONFIG.recording.defaultMimeType;
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = CONFIG.recording.fallbackMimeType;
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = isIOS() ? CONFIG.recording.iosMimeType : '';
        }
      }

      const maxTime = Number(tier.maxRecordTime) || 30;
      setTimeLeft(maxTime);

      const options = { 
        mimeType,
        videoBitsPerSecond: mobile 
          ? (isHD ? CONFIG.recording.mobileHdVideoBitsPerSecond : CONFIG.recording.mobileVideoBitsPerSecond)
          : (isHD ? CONFIG.recording.hdVideoBitsPerSecond : CONFIG.recording.videoBitsPerSecond)
      };

      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
        const url = URL.createObjectURL(blob);
        setVideoUrl(url);
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.src = url;
          videoRef.current.muted = false;
          videoRef.current.controls = true;
        }
        setRecording(false);
        toast.success(CONFIG.ui.toasts.recordingStopped);
      };

      mediaRecorder.start();
      setRecording(true);

      const startTime = Date.now();
      const timerInterval = setInterval(() => {
        const remaining = maxTime - Math.floor((Date.now() - startTime) / 1000);
        setTimeLeft(remaining);
        if (remaining <= 0) {
          clearInterval(timerInterval);
          mediaRecorder.state === 'recording' && stopRecording();
        }
      }, 1000);
    } catch (error) {
      toast.error(CONFIG.ui.toasts.recordingStartFailed);
      setRecording(false);
    }
  };

  const stopRecording = () => mediaRecorderRef.current?.state === 'recording' && mediaRecorderRef.current.stop();

  const generateThumbnail = (videoElement) => new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    canvas.getContext('2d').drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.7);
  });

  const uploadVideo = async () => {
    const user = auth.currentUser;
    if (!user || !chunksRef.current.length || !title.trim()) return;
    setLoading(true);
    try {
      const videoBlob = new Blob(chunksRef.current, { type: mediaRecorderRef.current.mimeType });
      const timestamp = Date.now();
      const vRef = storageRef(storage, `videos/${user.uid}/${timestamp}_video.webm`);
      const vUploadTask = uploadBytesResumable(vRef, videoBlob);
      
      vUploadTask.on('state_changed', (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)));
      await vUploadTask;
      const finalVideoUrl = await getDownloadURL(vRef);

      let finalThumbUrl = '/default-thumbnail.jpg';
      try {
        const thumbBlob = await generateThumbnail(videoRef.current);
        const tRef = storageRef(storage, `thumbnails/${user.uid}/${timestamp}_thumb.jpg`);
        await uploadBytesResumable(tRef, thumbBlob);
        finalThumbUrl = await getDownloadURL(tRef);
      } catch (e) {}

      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await transaction.get(userRef);
        const newVideoRef = doc(collection(db, 'videos'));
        transaction.set(newVideoRef, {
          userId: user.uid,
          title: title.trim(),
          videoUrl: finalVideoUrl,
          thumbnailUrl: finalThumbUrl,
          createdAt: serverTimestamp(),
          duration: CONFIG.tiers[userData.subscriptionTier || 'free'].maxRecordTime,
          mimeType: mediaRecorderRef.current.mimeType
        });
        transaction.update(userRef, { videoCount: (userDoc.data().videoCount || 0) + 1 });
      });

      const hasVanityId = !!userData?.vanityId;
      const hasPrimaryVideo = !!userData?.primaryVideoId;
      if (!hasVanityId) toast.success("🎉 Video Saved! Next: Claim your link in Profile.", { autoClose: 15000 });
      else if (!hasPrimaryVideo) toast.success("🎉 Video Saved! Set it as Primary in Dashboard.", { autoClose: 10000 });
      else toast.success(CONFIG.ui.toasts.videoSaved);
      
      navigate('/dashboard');
    } catch (error) {
      toast.error(CONFIG.ui.toasts.videoUploadFailed);
    } finally {
      setLoading(false);
    }
  };

  const isLimitReached = (() => {
    if (!userData) return false;
    const tier = CONFIG.tiers[userData.subscriptionTier || 'free'] || CONFIG.tiers.free;
    return (userData.videoCount || 0) >= tier.maxUploads;
  })();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <Card padding="p-0 overflow-hidden">
          <div className="md:flex">
            <div className="md:w-2/3 bg-gray-900 relative overflow-hidden border-r border-gray-100">
              {/* Aspect Ratio Container (Tailwind 2.x plugin style) */}
              <div className="aspect-w-16 aspect-h-9 w-full bg-gray-900">
                <div className="w-full h-full">
                  {/* Video Element */}
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    className={`w-full h-full object-cover z-10 ${recording ? 'ring-4 ring-destructive ring-inset' : ''} ${!cameraReady && !videoUrl ? 'hidden' : 'block'}`} 
                  />

                  {/* Professional Placeholder */}
                  {!cameraReady && !videoUrl && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-gray-800 text-gray-300 p-6 text-center">
                      <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mb-4 border border-gray-600 shadow-inner">
                        <Video className="w-8 h-8 text-brand-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Ready to record?</h3>
                      <p className="max-w-xs text-sm text-gray-400 leading-relaxed">
                        Click <strong className="text-brand-400 font-bold">"Enable Camera"</strong> to start. Your intro reel will be high quality and ready to share.
                      </p>
                    </div>
                  )}

                  {countdown !== null && (
                    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/60">
                      <div className="text-white text-9xl font-bold animate-ping">{countdown}</div>
                    </div>
                  )}

                  {recording && (
                    <div className="absolute top-4 right-4 z-30">
                      <TimerDisplay 
                        remainingTime={timeLeft} 
                        maxTime={userData?.subscriptionTier ? (CONFIG.tiers[userData.subscriptionTier]?.maxRecordTime || 30) : 30}
                        color={timeLeft < 10 ? 'red' : 'white'}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="md:w-1/3 p-8 flex flex-col justify-center bg-white">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{videoUrl ? 'Review & Save' : 'Record Video'}</h2>

              <div className="space-y-4">
                {!cameraReady && !recording && !videoUrl && (
                  <Button className="w-full" onClick={initializeCamera} icon={Camera} size="lg">Enable Camera</Button>
                )}
                
                {!videoUrl && cameraReady && !recording && userData && (
                  <>
                    {isLimitReached ? (
                      <div className="space-y-4">
                        <Alert variant="warning" title="Limit Reached">You've used your 1 free video. Upgrade to record more!</Alert>
                        <Button className="w-full" variant="secondary" onClick={() => navigate('/profile?upgrade=true')} icon={Zap} size="lg">Upgrade Now</Button>
                      </div>
                    ) : (
                      <Button className="w-full" variant="destructive" onClick={startRecording} icon={Circle} size="lg">Start Recording</Button>
                    )}
                  </>
                )}
                
                {recording && (
                  <Button className="w-full shadow-lg animate-pulse" variant="destructive" onClick={stopRecording} icon={Square} size="lg">Stop Recording</Button>
                )}
                
                {videoUrl && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                    <Input label="Video Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Introduction for Google" />
                    <Button className="w-full" onClick={uploadVideo} disabled={loading || !title.trim()} loading={loading} icon={Save} size="lg">Save Video</Button>
                    <Button variant="ghost" className="w-full" onClick={() => { setVideoUrl(''); setTitle(''); logRerecord(); initializeCamera(); }} icon={RefreshCcw}>Record Again</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default RecordVideo;
